import React from 'react';

export type StatusTone = 'exito' | 'aviso' | 'peligro' | 'info' | 'neutro' | 'marca';

const TONE_DOT: Record<StatusTone, string> = {
    exito: 'bg-exito',
    aviso: 'bg-aviso',
    peligro: 'bg-peligro',
    info: 'bg-navy-600',
    neutro: 'bg-acero-300',
    marca: 'bg-brand-400',
};

/**
 * Mapa de estatus del dominio → tono semántico.
 * Cubre proyectos, estimados, propuestas, facturas y leads, en inglés y en
 * español: la app mezcla ambos idiomas y un estatus sin coincidencia caía
 * silenciosamente en gris, dejando tablas enteras sin color de estado.
 * "in progress" se evalúa ANTES que "progress"→éxito para que no lo capture
 * la regla de completado.
 */
export function toneForStatus(status: string): StatusTone {
    const s = (status || '').toLowerCase();
    if (/(in progress|working|ongoing|started|en progreso|en curso|activo|active)/.test(s)) return 'info';
    if (/(completed|complete|paid|approved|accepted|won|signed|closed won|completado|pagado|aprobado|aceptado|ganado|firmado|listo)/.test(s)) return 'exito';
    if (/(cancelled|canceled|overdue|declined|rejected|lost|failed|expired|cancelado|vencido|rechazado|perdido|fallido|expirado)/.test(s)) return 'peligro';
    if (/(pending|scheduled|sent|draft|in review|awaiting|open|new|pendiente|programado|enviado|borrador|en revisión|revision|nuevo|por vencer)/.test(s)) return 'aviso';
    return 'neutro';
}

interface StatusDotProps {
    /** Texto del estatus. También determina el tono si no se pasa `tone`. */
    children: React.ReactNode;
    tone?: StatusTone;
    /** Anima el punto (solo para estados verdaderamente en vivo) */
    pulse?: boolean;
    className?: string;
}

/**
 * StatusDot — Sistema 2.0.
 * Punto de color + texto plano. Reemplaza los chips con fondo de color:
 * el color informa sin gritar y la fila se mantiene tranquila.
 */
export const StatusDot: React.FC<StatusDotProps> = ({ children, tone, pulse = false, className = '' }) => {
    const resolved = tone ?? toneForStatus(typeof children === 'string' ? children : '');
    return (
        <span className={`inline-flex items-center gap-2 text-[13px] font-medium text-acero-700 whitespace-nowrap ${className}`}>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${TONE_DOT[resolved]} ${pulse ? 'animate-pulse' : ''}`} aria-hidden="true" />
            {children}
        </span>
    );
};
